import React, { useEffect, useMemo, useState } from "react";
import { Block } from "@randajan/jet-react/dom/block";
import { usePopSelf } from "@randajan/jet-react/dom/modal";
import { Alert, Box, ButtonBase, FormHelperText, Typography } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";

import { Say } from "../../../../arc/locale/consts.js";
import { validateLocaleForm } from "../../../../arc/forms/localeForms.js";
import { bifrost } from "../../../assets/bifrost.js";
import { BlimpMuiFormActions } from "../../../elements/BlimpMuiFormActions/BlimpMuiFormActions";
import { BlimpMuiFormSection } from "../../../elements/BlimpMuiFormSection/BlimpMuiFormSection";
import { useBeforeUnload } from "../../../assets/hooks/useBeforeUnload.jsx";
import { Locale } from "../../../assets/Locale.js";
import { LocaleFlag } from "../LocaleFlag/LocaleFlag";
import {
    createLocalePayload,
    createLocaleValues,
    getLocaleErrorId,
    getLocaleFieldError,
    localeOptions,
    mergeLocaleValidation,
    validationFromReply
} from "./localeFormTools";

import "./LocaleForm.scss";
import { store } from "../../../assets/bases/store.js";

const emptyTouched = Object.freeze({});
const touchAllFields = _=>({ value:true });

export const LocaleForm = (props)=>{
    const { userIsLogged } = props;
    const popSelf = usePopSelf();

    const currentLocale = Locale.useId();
    const initialValues = useMemo(()=>createLocaleValues(currentLocale), [currentLocale]);
    const [ values, setValues ] = useState(initialValues);
    const [ touched, setTouched ] = useState(emptyTouched);
    const [ submitState, setSubmitState ] = useState("");
    const [ serverValidation, setServerValidation ] = useState(null);
    const [ submittingAction, setSubmittingAction ] = useState("");

    const say = useMemo(()=>Locale.select(values.value), [values.value]);


    useEffect(()=>{
        setValues(initialValues);
        setTouched(emptyTouched);
        setServerValidation(null);
        setSubmitState("");
    }, [initialValues]);

    useBeforeUnload(values.value !== initialValues.value || !!submittingAction);

    const close = _=>popSelf?.down();
    const localValidation = useMemo(()=>validateLocaleForm(createLocalePayload(values, false)), [values]);
    const validation = useMemo(()=>mergeLocaleValidation(localValidation, serverValidation), [localValidation, serverValidation]);
    const fieldError = getLocaleFieldError(validation);
    const showFieldError = !!fieldError && (submitState === "validationError" || touched.value);

    const setLocale = value=>{
        setValues({ value });
        setTouched({ value:true });
        setServerValidation(null);
        setSubmitState("");
    };

    const submit = async (event, isPermanent=true)=>{
        event.preventDefault();
        const result = validateLocaleForm(createLocalePayload(values, isPermanent));
        if (!result.isValid) {
            setServerValidation(null);
            setTouched(touchAllFields());
            setSubmitState("validationError");
            return;
        }

        setSubmittingAction(isPermanent ? "permanent" : "temporary");
        setSubmitState("");
        try {
            const reply = await bifrost.tx("/locale/set", result.values);
            if (!reply?.error) {
                if (isPermanent && !userIsLogged) {
                    store.accept(["locale"]);
                    store.set("locale", result.values.value);
                }
                setSubmittingAction("");
                close();
                return;
            }

            const serverResult = validationFromReply(reply);
            if (serverResult) {
                setServerValidation(serverResult);
                setTouched(touchAllFields());
                setSubmitState("validationError");
            } else {
                setSubmitState("submitError");
            }
        } catch(err) {
            setSubmitState("submitError");
        }
        setSubmittingAction("");
    };

    const isBusy = !!submittingAction;
    const actions = [
        {
            disabled:isBusy || !localeOptions.length,
            icon:<AccessTimeOutlinedIcon />,
            key:"temporary",
            label:submittingAction === "temporary" ? say("localeFormActionSaving") : say("localeFormActionTemporary"),
            onClick:event=>submit(event, false)
        },
        {
            disabled:isBusy || !localeOptions.length,
            icon:<SaveOutlinedIcon />,
            key:"permanent",
            label:submittingAction === "permanent" ? say("localeFormActionSaving") : say("localeFormActionPermanent"),
            primary:true,
            type:"submit"
        }
    ];

    return (
        <Block level={0} className="LocaleForm popScreen" caption={say("localeFormCaption")}>
            <Box className="BlimpMuiForm" component="form" onSubmit={submit} noValidate>
                {submitState === "validationError" && (
                    <Alert severity="error">{say("localeFormErrorValidation")}</Alert>
                )}
                {submitState === "submitError" && (
                    <Alert severity="error">{say("localeFormErrorSubmit")}</Alert>
                )}
                <Box className="BlimpMuiForm-sections">
                    <BlimpMuiFormSection bodyClassName="LocaleForm-options" icon={TranslateOutlinedIcon} title={say("localeFormSectionLanguage")}>
                        {localeOptions.map(option=>{
                            const isSelected = option.value === values.value;
                            const label = say(Say.id("locale", "form", "option", option.value));
                            return (
                                <ButtonBase
                                    aria-pressed={isSelected}
                                    className={isSelected ? "LocaleForm-option isSelected" : "LocaleForm-option"}
                                    key={option.value}
                                    onClick={_=>setLocale(option.value)}
                                    type="button"
                                >
                                    <LocaleFlag label={label} locale={option.value} selected={isSelected} />
                                    <Typography className="LocaleForm-label">{label}</Typography>
                                </ButtonBase>
                            );
                        })}
                        {showFieldError && (
                            <FormHelperText className="LocaleForm-fieldError" error>
                                {say.or(getLocaleErrorId(fieldError), say("localeFormValidationInvalid"))}
                            </FormHelperText>
                        )}
                    </BlimpMuiFormSection>
                </Box>
                <BlimpMuiFormActions actions={actions} className="LocaleForm-actions" />
            </Box>
        </Block>
    );
};
